import { createSlice } from '@reduxjs/toolkit';

type LiveChatNotification = {
  gameID: number;
  playerGUID?: string;
  unreadMessageCount: number;
  // The newest message the count covers. The serverless pushes also carry its
  // timestamp (ms); the legacy engine sends the GUID only.
  lastUnreadMessageGUID?: string;
  lastUnreadMessageTs?: number;
  // The newest message read at this table: set by clearLiveChatNotification
  // and, on the serverless path, by the server's read watermark (EN-10782).
  // Held apart from lastUnreadMessageGUID so a newer unread message or a
  // server zero can't overwrite it.
  lastReadMessageGUID?: string;
  lastReadMessageTs?: number;
};

export type ReadThrough = { readMessageGUID?: string; readMessageTs?: number };

const isTs = (ts: unknown): ts is number => typeof ts === 'number' && Number.isFinite(ts) && ts > 0;

// Whether a read covers a message: the message is older, or the same ms and
// the very message read. Two messages in one ms are told apart by GUID, as
// the server does (EN-10782). Exported for unit testing.
export const isCoveredByRead = (
  messageTs: number,
  messageGUID: string | undefined,
  readTs: number,
  readGUID: string | undefined
): boolean =>
  messageTs < readTs || (messageTs === readTs && !!messageGUID && messageGUID === readGUID);

// EN-10782: the broadcasters read the unread record on a loop, so a snapshot
// read before a clear can arrive after it, and a zero for an older read can
// arrive after a newer message's count. Either would put the wrong badge up.
// - A positive count for a message the last read covers is stale.
// - A zero whose read doesn't cover the newer unread message held is stale.
// Ordered by timestamp when both sides carry one, the same rule the server
// applies; the engine sends GUIDs only, so a count for the very message last
// read is the fallback. A count with neither is applied as before.
// Exported for unit testing.
export const isStaleAfterRead = (
  current: LiveChatNotification | undefined,
  incoming: LiveChatNotification
): boolean => {
  if (incoming.unreadMessageCount > 0) {
    const unreadTs = incoming.lastUnreadMessageTs;
    const readTs = current?.lastReadMessageTs;
    if (isTs(unreadTs) && isTs(readTs)) {
      return isCoveredByRead(
        unreadTs,
        incoming.lastUnreadMessageGUID,
        readTs,
        current?.lastReadMessageGUID
      );
    }
    return (
      !!incoming.lastUnreadMessageGUID &&
      incoming.lastUnreadMessageGUID === current?.lastReadMessageGUID
    );
  }
  const heldUnreadTs = current?.unreadMessageCount ? current.lastUnreadMessageTs : undefined;
  const readTs = incoming.lastReadMessageTs;
  return (
    isTs(heldUnreadTs) &&
    isTs(readTs) &&
    !isCoveredByRead(
      heldUnreadTs,
      current?.lastUnreadMessageGUID,
      readTs,
      incoming.lastReadMessageGUID
    )
  );
};

// The later of two reads; one without a timestamp only replaces one without.
const laterRead = (
  current: LiveChatNotification | undefined,
  incoming: Pick<LiveChatNotification, 'lastReadMessageGUID' | 'lastReadMessageTs'>
): Pick<LiveChatNotification, 'lastReadMessageGUID' | 'lastReadMessageTs'> => {
  const currentTs = current?.lastReadMessageTs;
  const incomingTs = incoming.lastReadMessageTs;
  const currentRead = {
    lastReadMessageGUID: current?.lastReadMessageGUID,
    lastReadMessageTs: currentTs
  };
  if (!incoming.lastReadMessageGUID && !isTs(incomingTs)) return currentRead;
  if (isTs(currentTs) && (!isTs(incomingTs) || incomingTs < currentTs)) return currentRead;
  return {
    lastReadMessageGUID: incoming.lastReadMessageGUID,
    lastReadMessageTs: incoming.lastReadMessageTs
  };
};

export const liveChatNotificationsSlice = createSlice({
  name: 'liveChatNotifications',
  initialState: [] as LiveChatNotification[],
  reducers: {
    // Merge the incoming notifications into the existing per-table state,
    // keyed by gameID, rather than replacing the whole array. Each table's
    // banner is independent: a broadcast that only carries the table that
    // just changed must not wipe the banners of the other open tables.
    // (Upserting also naturally handles clears, which arrive as an entry
    // with unreadMessageCount = 0.) gameID is stored as a number: the
    // serverless pushes send it as a string, the engine as a number.
    updateLiveChatNotifications: (state, action) => {
      const incoming: LiveChatNotification[] = Array.isArray(action.payload) ? action.payload : [];
      if (incoming.length === 0) return state;

      const byGameID = new Map<number, LiveChatNotification>();
      state.forEach((n) => byGameID.set(Number(n.gameID), n));
      incoming.forEach((n) => {
        const gameID = Number(n.gameID);
        const current = byGameID.get(gameID);
        if (!isStaleAfterRead(current, n)) {
          byGameID.set(gameID, { ...n, gameID, ...laterRead(current, n) });
        }
      });

      return Array.from(byGameID.values());
    },
    // Optimistically zero a single table's unread count the moment the player
    // opens/reads its chat, so the badge clears immediately rather than waiting
    // for the next server broadcast. Other tables' banners are left untouched.
    // Records the read for isStaleAfterRead: the newest message displayed when
    // given, else the newest notified one. Takes a gameID, or { gameID, ... }.
    clearLiveChatNotification: (state, action) => {
      const { gameID: rawGameID, ...read }: { gameID: number | string } & ReadThrough =
        typeof action.payload === 'object' && action.payload !== null
          ? action.payload
          : { gameID: action.payload };
      const gameID = Number(rawGameID);
      return state.map((n) =>
        Number(n.gameID) === gameID
          ? {
              ...n,
              unreadMessageCount: 0,
              ...laterRead(n, {
                lastReadMessageGUID:
                  read.readMessageGUID ?? n.lastUnreadMessageGUID ?? n.lastReadMessageGUID,
                lastReadMessageTs: read.readMessageTs
              })
            }
          : n
      );
    }
  }
});

export const { updateLiveChatNotifications, clearLiveChatNotification } =
  liveChatNotificationsSlice.actions;

export default liveChatNotificationsSlice.reducer;
